<template>
  <div class="fuel-equipment-selector">
    <label class="block text-sm font-medium text-gray-700 mb-2">
      Select Equipment <span class="text-red-500">*</span>
    </label>

    <div class="">
      <select
        class="w-full p-2 border border-gray-300 rounded-md"
        :value="selectedEquipmentId === undefined ? '' : String(selectedEquipmentId)"
        @change="onSelectChange"
        aria-label="Select equipment"
      >
        <option value="">-- Select equipment --</option>
        <option
          v-for="equipment in fuelEquipment"
          :key="equipment.id"
          :value="String(equipment.id)"
        >
          {{ equipment.name }} — {{ equipment.type }} • {{ equipment.model }}
        </option>
      </select>

      <div
        v-if="selectedEquipmentId && equipmentStats[selectedEquipmentId]"
        class="mt-3 p-2 bg-gray-100 rounded text-xs"
      >
        <div class="flex justify-between">
          <span>Total Fuel:</span>
          <span class="font-medium"
            >{{ (equipmentStats[selectedEquipmentId]?.total_fuel_used ?? 0).toFixed(1) }}L</span
          >
        </div>
        <div class="flex justify-between">
          <span>Avg Cost/L:</span>
          <span class="font-medium"
            >tsh{{
              (equipmentStats[selectedEquipmentId]?.average_cost_per_liter ?? 0).toFixed(2)
            }}</span
          >
        </div>
      </div>
    </div>

    <p v-if="error" class="text-red-500 text-xs mt-2">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useEquipmentStore } from '@/stores/equipmentStore'
import { useFuelStore } from '@/stores/fuelStore'

interface Props {
  modelValue?: number
}

interface Emits {
  (e: 'update:modelValue', value: number): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const equipmentStore = useEquipmentStore()
const fuelStore = useFuelStore()

const selectedEquipmentId = ref<number | undefined>(props.modelValue)

// keep local selection in sync when parent (v-model) changes, e.g. when editing a transaction
watch(
  () => props.modelValue,
  (val) => {
    selectedEquipmentId.value = val === undefined ? undefined : Number(val)
  },
  { immediate: true },
)
const error = ref<string>('')

const fuelEquipment = computed(() => {
  const list = Array.isArray(equipmentStore.equipment) ? equipmentStore.equipment : []
  return list.filter((eq) => eq && eq.requires_fuel)
})

type EqStat = { equipment_id: number; total_fuel_used: number; average_cost_per_liter: number }

const equipmentStats = computed(() => {
  const stats: { [key: number]: EqStat | undefined } = {}
  const arr = Array.isArray(fuelStore.equipmentStats) ? fuelStore.equipmentStats : []
  arr.forEach((stat) => {
    if (stat && typeof stat.equipment_id === 'number') {
      stats[stat.equipment_id] = stat
    }
  })
  return stats
})

onMounted(() => {
  equipmentStore.fetchEquipment()
  fuelStore.fetchEquipmentStats()
})

const onSelectChange = (e: Event) => {
  const val = (e.target as HTMLSelectElement).value
  if (!val) {
    selectedEquipmentId.value = undefined
    emit('update:modelValue', undefined as unknown as number)
    return
  }
  const id = Number(val)
  selectedEquipmentId.value = Number.isNaN(id) ? undefined : id
  if (!Number.isNaN(id)) emit('update:modelValue', id)
  error.value = ''
}

// Validation
const validate = (): boolean => {
  if (!selectedEquipmentId.value) {
    error.value = 'Please select equipment'
    return false
  }
  error.value = ''
  return true
}

// Allow parent to programmatically set the selected equipment (used when editing a transaction)
const setSelected = (id?: number) => {
  selectedEquipmentId.value = id === undefined ? undefined : Number(id)
  // keep parent v-model in sync
  if (id !== undefined && id !== null) emit('update:modelValue', Number(id))
}

defineExpose({ validate, setSelected })
</script>
